import {
    RequestStatus,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Spinner } from '../ui/loaders/Spinner.tsx';
import { ChangePasswordForm } from './ChangePasswordForm.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { useNavigate } from 'react-router-dom';
import {
    PasswordChangeTokenSource,
    SourceLoginConfirmLabel,
    useNavigateToSourceLogin,
} from './ChangePasswordController.tsx';

export function ConnectedChangePasswordFormController({
    token,
    source,
}: {
    token: string;
    source: PasswordChangeTokenSource;
}) {
    const navigate = useNavigate();

    const [newPasswordState, postNewPassword] = useServerWrite<
        { password: string; token: string },
        object
    >(() => ({
        url: '/ui-api/operator-account/tokenized-password-change',
    }));

    const onConfirm = useNavigateToSourceLogin(source);

    switch (newPasswordState.status) {
        case RequestStatus.NEVER_EXECUTED:
            return (
                <ChangePasswordForm
                    source={source}
                    onSave={password => postNewPassword({ password, token })}
                    onCancel={() => navigate('/')}
                />
            );

        case RequestStatus.PENDING:
            return <Spinner loading={true} />;

        case RequestStatus.ERROR:
            return (
                <ConfirmationDialog
                    title={
                        <Localized
                            de="Fehler"
                            fr="Erreur"
                            it="Errore"
                            en="Error"
                        />
                    }
                    open={true}
                    dialogVersion={ConfirmationDialogVersions.ERROR}
                    confirmLabel={
                        <Localized
                            de="Schliessen"
                            fr="Fermer"
                            it="Chiudi"
                            en="Close"
                        />
                    }
                    onConfirm={() => navigate(0)}
                    closeDisabled={true}
                >
                    <p>
                        <Localized
                            de={`Die Anfrage konnte nicht übermittelt werden (HTTP ${newPasswordState.httpStatusCode}). Bitte versuchen Sie später nochmals.`}
                            fr={`La requête n'a pas pu être envoyée (HTTP ${newPasswordState.httpStatusCode}). Veuillez réessayer plus tard.`}
                            it={`La richiesta non ha potuto essere inviata (HTTP ${newPasswordState.httpStatusCode}). Vogliate riprovare più tardi.`}
                            en={`The request could not be sent (HTTP ${newPasswordState.httpStatusCode}). Please try again later.`}
                        />
                    </p>
                </ConfirmationDialog>
            );

        case RequestStatus.SUCCESS:
            return (
                <ConfirmationDialog
                    title={
                        <Localized
                            de="Passwort geändert"
                            fr="Mot de passe modifié"
                            it="Password modificata"
                            en="Password changed"
                        />
                    }
                    open={true}
                    dialogVersion={ConfirmationDialogVersions.SUCCESS}
                    confirmLabel={<SourceLoginConfirmLabel source={source} />}
                    onConfirm={onConfirm}
                    closeDisabled={true}
                >
                    <p>
                        <Localized
                            de="Das neue Passwort wurde erfolgreich gespeichert."
                            fr="Le nouveau mot de passe a été enregistré avec succès."
                            it="La nuova password è stata salvata con successo."
                            en="The new password has been saved successfully."
                        />
                    </p>
                </ConfirmationDialog>
            );
    }
}
